import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ClipboardListIcon, FileTextIcon, PencilLineIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmptyState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Card, CardBody, SectionTitle } from '@/components/ui/card';
import { ReceiptSheet } from '@/features/visit/receipt-sheet';
import { dateId, rupiah, timeId } from '@/lib/format';
import { openField } from '@/offline/crypto';
import { db } from '@/offline/db';
import { getReceipt, type LocalReceipt, listReceipts } from '@/offline/receipts';
import { useOfflineQuery } from '@/offline/use-offline-query';
import { listDrafts } from '@/offline/visit-draft';

export const Route = createFileRoute('/_app/kunjungan/')({
  validateSearch: (search: Record<string, unknown>) => ({
    kuitansi: typeof search.kuitansi === 'string' ? search.kuitansi : undefined,
  }),
  component: VisitListPage,
});

const RESULT_LABELS: Record<string, string> = {
  BAYAR: 'Bayar',
  JANJI_BAYAR: 'Janji bayar',
  TIDAK_BERTEMU: 'Tidak bertemu',
  PINDAH_ALAMAT: 'Pindah alamat',
  MENOLAK_MEMBAYAR: 'Menolak membayar',
};

interface VisitRow {
  id: string;
  loanId: string;
  customerName: string;
  loanNumber: string;
  checkinAt: string;
  result: string;
  pending: boolean;
}

/**
 * Daftar "Kunjungan Saya" (T-P7-25).
 *
 * Status kirim ditampilkan per item, bukan hanya sebagai angka di header:
 * pertanyaan collector di akhir hari bukan "berapa yang belum terkirim"
 * melainkan "kunjungan mana yang belum terkirim", dan hanya yang kedua yang
 * dapat ditindaklanjuti.
 */
function VisitListPage() {
  const { kuitansi } = Route.useSearch();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<LocalReceipt | null>(null);

  useEffect(() => {
    if (kuitansi) void getReceipt(kuitansi).then(setReceipt);
    else setReceipt(null);
  }, [kuitansi]);

  const { data: drafts } = useOfflineQuery(() => listDrafts(), []);
  const { data: receipts } = useOfflineQuery(() => listReceipts(), []);
  const { data: visits, loading } = useOfflineQuery(async () => {
    const rows = await db.visitHistory.orderBy('checkinAt').reverse().limit(60).toArray();
    const loans = await db.loans.bulkGet(rows.map((row) => row.loanId));
    const customers = await db.customers.bulkGet(rows.map((row) => row.customerId));

    return Promise.all(
      rows.map(async (row, index) => ({
        id: row.id,
        loanId: row.loanId,
        loanNumber: loans[index]?.loanNumber ?? '—',
        customerName: customers[index] ? await openField(customers[index].fullName) : '—',
        checkinAt: row.checkinAt,
        result: row.result,
        pending: row.pending === true,
      })) satisfies Promise<VisitRow>[],
    );
  }, []);

  if (loading) return <ListSkeleton />;

  const today = new Date().toDateString();
  const todayVisits = (visits ?? []).filter(
    (visit) => new Date(visit.checkinAt).toDateString() === today,
  );
  const earlier = (visits ?? []).filter(
    (visit) => new Date(visit.checkinAt).toDateString() !== today,
  );

  return (
    <div className="space-y-4 px-4 py-4">
      {drafts && drafts.length > 0 ? (
        <section className="space-y-2">
          <SectionTitle>Draf belum selesai</SectionTitle>
          {drafts.map((draft) => (
            <Link
              key={draft.loanId}
              to="/kunjungan/$loanId"
              params={{ loanId: draft.loanId }}
              className="flex items-center gap-3 rounded-card border border-warning/40 bg-warning/10 p-3 active:opacity-80"
            >
              <PencilLineIcon className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  Kunjungan langkah {draft.step} dari 5
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  Disimpan {timeId(draft.updatedAt)} · {draft.photos.length} foto
                </span>
              </span>
            </Link>
          ))}
        </section>
      ) : null}

      <section className="space-y-2">
        <SectionTitle>Hari ini</SectionTitle>
        {todayVisits.length === 0 ? (
          <EmptyState
            icon={ClipboardListIcon}
            title="Belum ada kunjungan hari ini"
            description="Mulai dari daftar nasabah, atau susun rute berdasarkan jarak."
            action={
              <Link to="/nasabah" className="text-sm text-brand underline underline-offset-2">
                Buka daftar nasabah
              </Link>
            }
          />
        ) : (
          todayVisits.map((visit) => <VisitCard key={visit.id} visit={visit} />)
        )}
      </section>

      {receipts && receipts.length > 0 ? (
        <section className="space-y-2">
          <SectionTitle>Kuitansi</SectionTitle>
          {receipts.slice(0, 10).map((entry) => (
            <button
              key={entry.paymentClientUuid}
              type="button"
              onClick={() =>
                void navigate({ to: '/kunjungan', search: { kuitansi: entry.paymentClientUuid } })
              }
              className="flex w-full items-center gap-3 rounded-card border border-border bg-surface p-3 text-left active:bg-muted"
            >
              <FileTextIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{entry.customerName}</span>
                <span className="block truncate font-mono text-xs text-muted-foreground">
                  {entry.finalNo ?? entry.temporaryNo}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-semibold tabular-nums">
                  {rupiah(entry.amount)}
                </span>
                {entry.finalNo === null ? <Badge tone="warning">Sementara</Badge> : null}
              </span>
            </button>
          ))}
        </section>
      ) : null}

      {earlier.length > 0 ? (
        <section className="space-y-2">
          <SectionTitle>Riwayat</SectionTitle>
          {earlier.slice(0, 30).map((visit) => (
            <VisitCard key={visit.id} visit={visit} showDate />
          ))}
        </section>
      ) : null}

      <ReceiptSheet
        receipt={receipt}
        open={receipt !== null}
        onClose={() => void navigate({ to: '/kunjungan', search: { kuitansi: undefined } })}
      />
    </div>
  );
}

function VisitCard({ visit, showDate = false }: { visit: VisitRow; showDate?: boolean }) {
  return (
    <Link to="/nasabah/$loanId" params={{ loanId: visit.loanId }}>
      <Card className="active:bg-muted">
        <CardBody className="space-y-1 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{visit.customerName}</p>
              <p className="truncate text-xs text-muted-foreground">{visit.loanNumber}</p>
            </div>
            <p className="shrink-0 text-xs text-muted-foreground">
              {showDate ? dateId(visit.checkinAt) : timeId(visit.checkinAt)}
            </p>
          </div>

          <div className="flex items-center gap-1.5">
            <Badge tone={visit.result === 'BAYAR' ? 'success' : 'neutral'}>
              {RESULT_LABELS[visit.result] ?? visit.result}
            </Badge>
            {visit.pending ? <Badge tone="warning">Menunggu kirim</Badge> : null}
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
